import { useState, useEffect, useCallback } from 'react';
import {
  zaloZnsTemplateService,
  ZaloZnsTemplate,
  ZaloZnsTemplateFilters,
  CreateZaloZnsTemplateDto,
  UpdateZaloZnsTemplateDto,
} from '../services/zalo-zns-template.service';

interface UseZaloZnsTemplatesReturn {
  templates: ZaloZnsTemplate[];
  loading: boolean;
  error: Error | null;
  meta: { page: number; limit: number; total: number; totalPages: number };
  filters: ZaloZnsTemplateFilters;
  setFilters: (filters: ZaloZnsTemplateFilters) => void;
  refetch: () => void;
  createTemplate: (data: CreateZaloZnsTemplateDto) => Promise<ZaloZnsTemplate>;
  updateTemplate: (id: string, data: UpdateZaloZnsTemplateDto) => Promise<ZaloZnsTemplate>;
  deleteTemplates: (ids: string[]) => Promise<void>;
  cloneTemplate: (id: string) => Promise<ZaloZnsTemplate>;
}

export const useZaloZnsTemplates = (
  initialFilters: ZaloZnsTemplateFilters = {},
): UseZaloZnsTemplatesReturn => {
  const [templates, setTemplates] = useState<ZaloZnsTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<ZaloZnsTemplateFilters>({
    page: 1,
    limit: 10,
    ...initialFilters,
  });
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await zaloZnsTemplateService.getAll(filters);
      setTemplates(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch Zalo ZNS templates'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const createTemplate = async (data: CreateZaloZnsTemplateDto): Promise<ZaloZnsTemplate> => {
    const template = await zaloZnsTemplateService.create(data);
    await fetchTemplates();
    return template;
  };

  const updateTemplate = async (
    id: string,
    data: UpdateZaloZnsTemplateDto,
  ): Promise<ZaloZnsTemplate> => {
    const template = await zaloZnsTemplateService.update(id, data);
    await fetchTemplates();
    return template;
  };

  const deleteTemplates = async (ids: string[]): Promise<void> => {
    await zaloZnsTemplateService.deleteMany(ids);
    await fetchTemplates();
  };

  const cloneTemplate = async (id: string): Promise<ZaloZnsTemplate> => {
    const template = await zaloZnsTemplateService.clone(id);
    await fetchTemplates();
    return template;
  };

  return {
    templates,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch: fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplates,
    cloneTemplate,
  };
};

export interface UseZaloZnsTemplateDetailReturn {
  template: ZaloZnsTemplate | null;
  loading: boolean;
  error: Error | null;
}

export const useZaloZnsTemplateDetail = (
  id: string | undefined,
): UseZaloZnsTemplateDetailReturn => {
  const [template, setTemplate] = useState<ZaloZnsTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    zaloZnsTemplateService
      .getById(id)
      .then(setTemplate)
      .catch((err) =>
        setError(err instanceof Error ? err : new Error('Failed to fetch template')),
      )
      .finally(() => setLoading(false));
  }, [id]);

  return { template, loading, error };
};
